import { useEffect, useState } from "react";
import axios from "axios";
import PublicationCard from "../../components/PublicationCard";
import ClipLoader from "react-spinners/ClipLoader";
import { toast } from "react-toastify";
import DropdownActions from "../../components/DropdownActions";
import { useNavigate } from "react-router-dom";

const Invitations = () => {
  const [invitations, setInvitations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editInvitationId, setEditInvitationId] = useState(null);
  const [editTitle, setEditTitle] = useState("");
  const [editAbstract, setEditAbstract] = useState("");
  const [editLoading, setEditLoading] = useState(false);
  const navigate = useNavigate()

  const token = localStorage.getItem("access_token");
  const BASE_URL = process.env.REACT_APP_BASE_URL;

  const handlePreview = (inv) => {
    navigate("/viewer", {
      state: {
        url: inv.publication?.upload_document,
        title: inv.publication?.title,
        description: inv.publication?.abstract || inv.publication?.description,
        type: "publication",
        userId: inv.publication?.user,
        postId: inv.publication?.id,
        is_Liked: inv.publication?.is_liked,
        likeCount: inv.publication?.total_likes,
        commentCount: inv.publication?.total_comments,
      },
    });
  };

  useEffect(() => {
    const fetchInvitations = async () => {
      try {
        const res = await axios.get(
          `${BASE_URL}/user/publication/view-received-invitation-list/`,
          { headers: { Authorization: `Bearer ${token}`,"x-api-key": process.env.REACT_APP_X_API_KEY  } }
        );
        setInvitations(res.data?.data || []);
      } catch (err) {
        console.error("Error fetching invitations", err);
      } finally {
        setLoading(false);
      }
    };
    fetchInvitations();
  }, [token]);

  const handleEditClick = (inv) => {
    if (!inv.can_edit) return;
    setEditInvitationId(inv.id);
    setEditTitle(inv.publication?.title || "");
    setEditAbstract(inv.publication?.abstract || "");
  };

  const handleEditSubmit = async (inv) => {
    if (!editTitle.trim()) {
      toast.error("Title cannot be empty");
      return;
    }

    try {
      setEditLoading(true);
      const res = await axios.patch(
        `${BASE_URL}/user/publication/publication/`,
        { id: inv.publication.id, title: editTitle, abstract: editAbstract },
        { headers: { Authorization: `Bearer ${token}`,"x-api-key": process.env.REACT_APP_X_API_KEY } }
      );

      if (res.status === 200 || res.status === 204) {
        toast.success("Publication updated successfully!");
        setInvitations((prev) =>
          prev.map((i) =>
            i.id === inv.id
              ? {
                  ...i,
                  publication: {
                    ...i.publication,
                    title: editTitle,
                    abstract: editAbstract,
                  },
                }
              : i
          )
        );
        setEditInvitationId(null);
      } else {
        toast.error(res.data?.message || "Failed to update");
      }
    } catch (err) {
      console.error(err);
      toast.error("Update failed");
    } finally {
      setEditLoading(false);
    }
  };

  const handleDecision = async (
    invitationId,
    senderId,
    publicationId,
    decision
  ) => {
    try {
      await axios.post(
        `${BASE_URL}/user/publication/collebrate-invite-action/`,
        {
          sender_id: senderId,
          publication_id: publicationId,
          decision,
        },
        {
          headers: { Authorization: `Bearer ${token}`,"x-api-key": process.env.REACT_APP_X_API_KEY  },
        }
      );

      setInvitations((prev) =>
        prev.map((inv) =>
          inv.id === invitationId ? { ...inv, status: decision } : inv
        )
      );

      toast.success(`Invitation ${decision}ed successfully`);
    } catch (err) {
      console.error(`Error updating invitation (${decision})`, err);
      toast.error("Failed to update invitation");
    }
  };

  if (loading)
    return (
      <div className="flex justify-center items-center min-h-[200px]">
        <ClipLoader color="#4A90E2" size={40} />
      </div>
    );

  if (!invitations.length) return <div className="w-full max-w-[1024px] flex flex-col justify-center items-center py-12 bg-white rounded-xl border border-gray-200">
  <div className="w-16 h-16 bg-[#e8f0f7] rounded-xl flex items-center justify-center mb-4">
    <svg 
      className="w-8 h-8 text-[#0f6abf]" 
      fill="none" 
      stroke="currentColor" 
      viewBox="0 0 24 24" 
      xmlns="http://www.w3.org/2000/svg"
    >
      <path 
        strokeLinecap="round" 
        strokeLinejoin="round" 
        strokeWidth="2" 
        d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z" 
      />
    </svg>
  </div>
  <h3 className="text-lg font-semibold text-gray-900 mb-2">No invitations received</h3>
  <p className="text-gray-500 text-sm mb-4">You haven't received any invitations yet</p>
</div>

  return (
   <div className="flex flex-col items-center w-full">
  <div className="bg-white border border-gray-200 w-full max-w-[1024px] p-2 md:p-6 mt-3 rounded-2xl shadow-sm">
    {/* Header */}
    <div className="flex items-center justify-between mb-6">
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 bg-[#e8f0f7] rounded-lg flex items-center justify-center">
          <svg className="w-5 h-5 text-[#0f6abf]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z" />
          </svg>
        </div>
        <div>
          <h2 className="text-lg font-semibold text-gray-900">Received Invitations</h2>
          <p className="text-sm text-gray-500 mt-0.5">Collaboration requests from other researchers</p>
        </div>
      </div>
    </div>

    {/* Invitations List */}
    <div className="space-y-4">
      {invitations
        .sort((a, b) =>
          a.status === "pending" && b.status !== "pending" ? -1 : 0
        )
        .map((inv) => (
          <div key={inv.id}>
            {editInvitationId === inv.id ? (
              <div className="bg-gray-50 border border-gray-200 rounded-xl p-6">
                <div className="space-y-4">
                  <input
                    type="text"
                    value={editTitle}
                    onChange={(e) => setEditTitle(e.target.value)}
                    placeholder="Edit title"
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl text-gray-900 placeholder:text-gray-400 focus:outline-none focus:border-[#0f6abf] focus:ring-2 focus:ring-[#0f6abf]/20 transition-all"
                  />
                  <textarea
                    value={editAbstract}
                    onChange={(e) => setEditAbstract(e.target.value)}
                    placeholder="Edit abstract"
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl text-gray-900 placeholder:text-gray-400 focus:outline-none focus:border-[#0f6abf] focus:ring-2 focus:ring-[#0f6abf]/20 resize-none min-h-[100px] transition-all"
                  />
                  <div className="flex gap-3 mt-2">
                    <button
                      className="flex-1 py-2.5 bg-[#0f6abf] text-white text-sm font-medium rounded-xl hover:bg-[#0a5ca8] transition-colors disabled:opacity-50"
                      onClick={() => handleEditSubmit(inv)}
                      disabled={editLoading}
                    >
                      {editLoading ? (
                        <div className="flex items-center justify-center gap-2">
                          <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                          Saving...
                        </div>
                      ) : (
                        "Save Changes"
                      )}
                    </button>
                    <button
                      className="flex-1 py-2.5 border border-gray-300 text-gray-700 text-sm font-medium rounded-xl hover:bg-gray-50 transition-colors"
                      onClick={() => setEditInvitationId(null)}
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              </div>
            ) : (
              <PublicationCard
                image={inv.publication?.upload_document}
                heading={inv.publication?.title}
                subtitle={inv.publication?.abstract}
                likeCount={inv.publication?.total_likes || 0}
                is_Liked={inv.publication?.is_liked || false}
                isPdf={true}
                author={inv.sender?.name}
                postId={inv.publication?.id}
                onPreview={() => handlePreview(inv)}
                dropdownActions={
                  inv.can_edit ? (
                    <div className="absolute top-[-60px] md:top-[-65px] right-4 md:right-2">
                    <DropdownActions
                      type="publication"
                      onEdit={() => handleEditClick(inv)}
                      showDelete={false}
                      showInvite={false}
                    />
                    </div>
                  ) : null
                }
              />
            )}
            
            {/* Pending Invitation Actions */}
            {inv.status === "pending" && (
              <div className="flex gap-3 mt-4 pl-4">
                <button
                  className="px-5 py-2.5 bg-green-500 text-white text-sm font-medium rounded-xl hover:bg-green-600 transition-colors flex items-center gap-2"
                  onClick={() =>
                    handleDecision(
                      inv.id,
                      inv.sender.id,
                      inv.publication.id,
                      "accept"
                    )
                  }
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                  </svg>
                  Accept Invitation
                </button>
                <button
                  className="px-5 py-2.5 bg-red-100 text-red-600 text-sm font-medium rounded-xl hover:bg-red-200 transition-colors flex items-center gap-2"
                  onClick={() =>
                    handleDecision(
                      inv.id,
                      inv.sender.id,
                      inv.publication.id,
                      "reject"
                    )
                  }
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                  Decline
                </button>
              </div>
            )}
          </div>
        ))}
    </div>
  </div>
</div>
  );
};

export default Invitations;
